/**
 * Store for managing members data
 *
 * IMPORTANT REACTIVITY NOTES:
 * 1. Always use $patch for state updates to ensure proper reactivity
 * 2. Cross-store dependencies are managed through subscriptions
 * 3. Tracking properties (_lastSyncedType, _lastSyncedCaptainIds) prevent infinite loops
 * 4. Captain and SubKrewe relationships are maintained bidirectionally
 */
import type { DocumentReference, QueryConstraint } from 'firebase/firestore'
import {
  collection,
  deleteDoc,
  deleteField, doc, getDoc, getDocs, getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useCurrentUser } from 'vuefire'
import { useSubscriptions } from '~/composables/useSubscriptions.js'
import type { Member, MemberFirestoreData } from '~/shared/types/member.js'
// Add type guard for redshirt restrictions
import { isRedshirt } from '~/shared/types/memberType.js'
import { deepTrim } from '~/shared/utils/sanitize.js'

export const useMembersStore = defineStore('members', {
  state: () => ({
    // Store members by document path for O(1) lookups
    members: {} as Record<string, Member>,
    captainsLoaded: false,
    loading: false,
  }),

  getters: {
    // Convert map to array for components that expect arrays
    membersArray(): Member[] {
      return Object.values(this.members)
    },

    // Get all captains (no need to fetch separately)
    captains(): Member[] {
      return this.membersArray.filter(m => m.member_type === 'captain')
    },

    // Get available captains (not already assigned)
    availableCaptains(): Member[] {
      return this.captains.filter(c => !c.subkrewe)
    },
  },

  actions: {
    /**
     * Convert Firestore document data to a Member object
     */
    convertToMember(docId: string, data: MemberFirestoreData, resolveSubkrewe = true): Member {
      // Handle date conversions
      const created_at = data.created_at ? data.created_at : new Date()
      const updated_at = data.updated_at ? data.updated_at : new Date()

      // Create the basic member without resolved subkrewe
      const member: Member = {
        id: docId,
        name: data.name || '',
        email: data.email || '',
        member_type: data.member_type,
        created_at,
        updated_at,
        // Initially null, will be resolved later if needed
        subkrewe: null,
        roles: data.roles,
        address: data.address,
        phone: data.phone,
        hide_phone: data.hide_phone,
        birthday: data.birthday,
        banned: data.banned,
        lifetime: data.lifetime,
        lifetime_reason: data.lifetime_reason,
        lifetime_since: data.lifetime_since,
        purchases: data.purchases,
        // Add this line to include children data
        children: data.children,
        // Redshirt-specific fields
        isRedshirtLieutenant: data.isRedshirtLieutenant ?? false,
        tshirt_size: data.tshirt_size,
      }

      // If no subkrewe reference or not resolving, return as is
      if (!data.subkrewe || !resolveSubkrewe) {
        return member
      }

      // Resolve subkrewe reference if requested
      const subkrewesStore = useSubKrewesStore()
      const subkrewePath = data.subkrewe.path
      const subkrewe = subkrewesStore.subkrewes[subkrewePath]

      if (subkrewe) {
        member.subkrewe = subkrewe
      }

      return member
    },

    /**
     * Convert a Member object to Firestore-compatible data format
     */
    convertToFirestoreData(member: Member): Partial<MemberFirestoreData> {
      const db = getFirestore()

      // Create a properly typed object for Firestoreg425
      const firestoreData: Partial<MemberFirestoreData> = {
        // Basic fields
        name: member.name,
        email: member.email,
        member_type: member.member_type,
        updated_at: serverTimestamp(),

        // Optional fields
        roles: member.roles,
        address: member.address,
        phone: member.phone,
        birthday: member.birthday,
        displayName: member.displayName,
        purchases: member.purchases,
        children: member.children,
        hide_phone: member.hide_phone,
        // Redshirt fields
        isRedshirtLieutenant: member.isRedshirtLieutenant,
        tshirt_size: member.tshirt_size,
      }

      // Remove any undefined fields, because Firestore doesn't like them
      for (const key in firestoreData) {
        if (firestoreData[key] === undefined) {
          firestoreData[key] = deleteField()
        }
      }

      // Handle subkrewe reference - convert object to DocumentReference
      if (member.subkrewe) {
        firestoreData.subkrewe = doc(db, 'subkrewes', member.subkrewe.id)
      }
      else {
        firestoreData.subkrewe = deleteField()
      }

      return firestoreData
    },

    /**
     * Toggle a redshirt's Lieutenant status
     * Only meaningful for member.member_type === 'redshirt'
     */
    async toggleRedshirtLieutenant(member: Member, makeLieutenant: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle lieutenant: invalid member object')
          return false
        }

        if (member.member_type !== 'redshirt') {
          console.warn(`[Members] Ignoring lieutenant toggle for non-redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        await updateDoc(memberRef, {
          isRedshirtLieutenant: makeLieutenant,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          state.members[memberPath].isRedshirtLieutenant = makeLieutenant
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling redshirt lieutenant for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Ensure all captains are loaded into the store
     */
    async fetchCaptains(): Promise<Member[]> {
      if (this.captainsLoaded) {
        return this.captains
      }

      this.loading = true
      try {
        const db = getFirestore()
        const captainsQuery = query(
          collection(db, 'members'),
          where('member_type', '==', 'captain'),
        )

        const snapshot = await getDocs(captainsQuery)

        // Use $patch for batch state updates
        this.$patch((state) => {
          snapshot.forEach((doc) => {
            const data = doc.data() as MemberFirestoreData
            // Don't try to resolve subkrewe yet (avoid circular dependency)
            const member = this.convertToMember(doc.id, data, false)
            // Store by document path
            state.members[doc.ref.path] = member
          })
          state.captainsLoaded = true
        })

        return this.captains
      }
      catch (error) {
        console.error('Error loading captains:', error)
        return []
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Fetch a member by their ID
     */
    async fetchMember(id: string, forceRefresh = false): Promise<Member | null> {
      try {
        // Check if already in store and not forcing refresh
        const path = `members/${id}`
        if (!forceRefresh && this.members[path]) {
          return this.members[path]
        }

        const db = useFirestore()
        const docRef = doc(db, 'members', id)
        const docSnap = await getDoc(docRef)

        if (!docSnap.exists()) {
          return null
        }

        const data = docSnap.data() as MemberFirestoreData

        // Create member without resolved subkrewe first
        const member = this.convertToMember(id, data, false)

        const subkrewe = await this.resolveSubkreweForMember(data.subkrewe)

        // Store in map by path using $patch
        this.$patch((state) => {
          member.subkrewe = subkrewe
          state.members[docRef.path] = member
        })

        return member
      }
      catch (error) {
        console.error(`Error fetching member ${id}:`, error)
        return null
      }
    },

    /**
     * Fetch all members
     */
    async fetchMembers(): Promise<Member[]> {
      this.loading = true

      try {
        const db = getFirestore()
        const membersCollection = collection(db, 'members')
        const querySnapshot = await getDocs(membersCollection)

        // Process all members first
        const membersToAdd: Record<string, Member> = {}

        const memberPromises = querySnapshot.docs.map(async (doc) => {
          const data = doc.data() as MemberFirestoreData
          const member = this.convertToMember(doc.id, data, false)
          // Resolve subkrewe
          member.subkrewe = await this.resolveSubkreweForMember(data.subkrewe)
          // Add to our local batch
          membersToAdd[doc.ref.path] = member
          return member
        })

        // Wait for all resolutions to complete
        await Promise.all(memberPromises)

        // Update the store with all members at once for better performance
        this.$patch((state) => {
          Object.assign(state.members, membersToAdd)
        })

        return Object.values(this.members)
      }
      catch (error) {
        console.error('Error fetching members:', error)
        return []
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Resolve the subkrewe for a member
     */
    async resolveSubkreweForMember(subkreweRef: DocumentReference | undefined | null): Promise<SubKrewe | null> {
      if (!subkreweRef) return null
      const subkrewesStore = useSubKrewesStore()

      // First check if already in store
      if (subkrewesStore.subkrewes[subkreweRef.path]) {
        return subkrewesStore.subkrewes[subkreweRef.path]
      }

      // Not in store, use the new method to fetch it
      return await subkrewesStore.fetchSubKreweByRef(subkreweRef)
    },

    /**
     * Update a member with new properties
     * Handles both Firestore updates and local state
     */
    async updateMember(member: Member, updates: Partial<Member>): Promise<boolean> {
      try {
        if (!member || !member.id) {
          console.error('Cannot update member: invalid member object or missing ID')
          return false
        }

        // Create a deep clone of the updates to avoid modifying the original object
        // and sanitize string fields (trim leading/trailing whitespace)
        const updatesClone = deepTrim({ ...updates })

        // Special handling for purchases to ensure we merge rather than replace
        if (updatesClone.purchases && member.purchases) {
          // Extract purchases from updates and remove from the clone
          const { purchases, ...otherUpdates } = updatesClone

          // Create merged purchases by combining existing and new purchases
          const mergedPurchases = {
            ...member.purchases,
          }

          // Merge per year
          if (purchases) {
            Object.keys(purchases).forEach((year) => {
              // Create a base object with required properties and default values
              const baseDefaults = {
                hasMembership: false,
                membershipPurchasedAt: null,
                hasVip: false,
                vipPurchasedAt: null,
                hasParking: false,
                orderIds: [],
              }

              mergedPurchases[year] = {
                ...baseDefaults,
                ...(member.purchases?.[year] || {}),
                ...(purchases[year] || {}),
              }
            })
          }

          // Create updatedMember with merged purchases
          const updatedMember = deepTrim({
            ...member,
            ...otherUpdates,
            purchases: mergedPurchases,
          })

          const memberPath = `members/${member.id}`
          const db = getFirestore()
          const memberRef = doc(db, 'members', member.id)

          // Use our conversion method to get Firestore-compatible data
          const firestoreData = this.convertToFirestoreData(updatedMember)
          // Update Firestore first
          await updateDoc(memberRef, firestoreData)

          // Handle name updates
          if (otherUpdates.name && otherUpdates.name !== member.name) {
            try {
              await withTokenRetry(async () => {
                return await $fetch('/api/members/update-auth-profile', {
                  method: 'POST',
                  body: {
                    userId: member.id,
                    displayName: otherUpdates.name,
                  },
                })
              })
            }
            catch (error) {
              console.error('Error updating Firebase Auth displayName:', error)
              // Continue with store update even if Auth update fails
            }
          }

          // Then update local state using $patch
          this.$patch((state) => {
            state.members[memberPath] = {
              ...updatedMember,
              updated_at: new Date(),
            }
          })

          // Handle subkrewe relationship synchronization
          if ('subkrewe' in updatesClone) {
            await this.syncSubkreweRelationship(member, updatedMember.subkrewe ?? null)
          }

          return true
        }
        else {
          // Default behavior when no purchases are being updated
          const updatedMember = deepTrim({ ...member, ...updates })
          const memberPath = `members/${member.id}`
          const db = getFirestore()
          const memberRef = doc(db, 'members', member.id)

          // Use our conversion method to get Firestore-compatible data
          const firestoreData = this.convertToFirestoreData(updatedMember)

          // Update Firestore first
          await updateDoc(memberRef, firestoreData)
          // If name is being updated, also update the Firebase Auth displayName
          if (updates.name && updates.name !== member.name) {
            try {
              await withTokenRetry(async () => {
                return await $fetch('/api/members/update-auth-profile', {
                  method: 'POST',
                  body: {
                    userId: member.id,
                    displayName: updates.name,
                  },
                })
              })
            }
            catch (error) {
              console.error('Error updating Firebase Auth displayName:', error)
              // Continue with store update even if Auth update fails
            }
          }

          // Then update local state using $patch
          this.$patch((state) => {
            state.members[memberPath] = {
              ...updatedMember,
              updated_at: new Date(),
            }
          })

          // Handle subkrewe relationship synchronization
          if ('subkrewe' in updates) {
            await this.syncSubkreweRelationship(member, updatedMember.subkrewe ?? null)
          }

          return true
        }
      }
      catch (error: unknown) {
        const firebaseError = error as { code?: string, message?: string }
        console.error('Error updating member:', error)
        console.error('Firebase error code:', firebaseError.code)
        console.error('Firebase error message:', firebaseError.message)
        console.error('Member ID:', member.id)
        console.error('Updates attempted:', JSON.stringify(updates, null, 2))
        return false
      }
    },

    /**
     * Helper method to synchronize subkrewe relationships when a member's subkrewe changes
     */
    async syncSubkreweRelationship(member: Member, newSubkrewe: SubKrewe | null): Promise<void> {
      const subkrewesStore = useSubKrewesStore()
      const oldSubkrewe = member.subkrewe

      // Only proceed if there's an actual change
      if (oldSubkrewe?.id === newSubkrewe?.id) return

      // Case 1: Member was in a subkrewe and is now removed or moved
      if (oldSubkrewe) {
        subkrewesStore.$patch((state) => {
          const oldSubkrewePath = `subkrewes/${oldSubkrewe.id}`
          if (state.subkrewes[oldSubkrewePath]?.members) {
            state.subkrewes[oldSubkrewePath].members
              = state.subkrewes[oldSubkrewePath].members.filter(m => m.id !== member.id)
          }
        })
      }

      // Case 2: Member is being added to a new subkrewe
      if (newSubkrewe) {
        const memberPath = `members/${member.id}`
        const currentMember = this.members[memberPath]

        subkrewesStore.$patch((state) => {
          const newSubkrewePath = `subkrewes/${newSubkrewe.id}`
          if (state.subkrewes[newSubkrewePath]) {
            if (!state.subkrewes[newSubkrewePath].members) {
              state.subkrewes[newSubkrewePath].members = []
            }

            // Remove old reference if exists
            state.subkrewes[newSubkrewePath].members
              = state.subkrewes[newSubkrewePath].members.filter(m => m.id !== member.id)

            // Add updated reference
            state.subkrewes[newSubkrewePath].members.push(currentMember)
          }
        })
      }
    },

    /**
     * Change a member's subkrewe
     * Handles bidirectional relationship
     * @param suppressNotifications If true, no notifications will be sent (useful when called from tasks)
     * @param actingUserId Optional ID of the user performing the action (for notifications)
     */
    async changeMemberSubKrewe(
      member: Member,
      newSubKrewe: SubKrewe | null,
    ): Promise<boolean> {
      try {
        if (!member || !member.id) {
          console.error('Cannot change subkrewe: invalid member')
          return false
        }

        // Block captains from changing subkrewes directly
        if (member.member_type === 'captain' && member.subkrewe && newSubKrewe) {
          console.error('Cannot change captain subkrewe directly')
          return false
        }

        // Update Firestore
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const firestoreData = this.convertToFirestoreData({
          ...member,
          subkrewe: newSubKrewe,
        })

        await updateDoc(memberRef, firestoreData)

        // Update in-memory state using $patch
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          state.members[memberPath] = {
            ...state.members[memberPath],
            subkrewe: newSubKrewe,
            updated_at: new Date(),
          } as Member
        })

        // Handle the subkrewes-side relationship
        await this.syncSubkreweRelationship(member, newSubKrewe)

        // Remove notification logic - this is now handled exclusively in removeMemberFromSubKrewe
        // This eliminates duplicate notifications when removing members

        return true
      }
      catch (error) {
        console.error(`Error changing member ${member.id} subkrewe:`, error)
        return false
      }
    },

    /**
     * Promotes a member to captain and assigns them to a SubKrewe in one atomic operation
     * This ensures we don't have captains without SubKrewes or vice versa
     */
    async promoteToCaptain(member: Member, subkrewe: SubKrewe): Promise<boolean> {
      try {
        // Block member from having multiple operations
        this.loading = true

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`
        const subkrewesStore = useSubKrewesStore()

        // IMPORTANT: If member is already in a subkrewe, remove them first
        if (member.subkrewe) {
          const oldSubkrewe = member.subkrewe

          // Remove member from old subkrewe in store state
          subkrewesStore.$patch((state) => {
            const oldSubkrewePath = `subkrewes/${oldSubkrewe.id}`
            if (state.subkrewes[oldSubkrewePath]?.members) {
              state.subkrewes[oldSubkrewePath].members
                = state.subkrewes[oldSubkrewePath].members.filter(m => m.id !== member.id)
            }
          })

          // Update Firestore to remove member from old subkrewe
          const oldSubkreweRef = doc(db, 'subkrewes', oldSubkrewe.id)
          const oldSubkreweMembers = oldSubkrewe.members?.filter(m => m.id !== member.id) || []
          await updateDoc(oldSubkreweRef, {
            members: oldSubkreweMembers.map(m => doc(db, 'members', m.id)),
            updated_at: serverTimestamp(),
          })
        }

        // 1. Update the member type to captain in Firestore
        await updateDoc(memberRef, {
          member_type: 'captain',
          // Remove any existing subkrewe reference (captain->subkrewe relationship is maintained by the subkrewe)
          subkrewe: deleteField(),
          updated_at: serverTimestamp(),
        })

        // 2. Update local state
        this.$patch((state) => {
          state.members[memberPath] = {
            ...state.members[memberPath],
            member_type: 'captain',
            subkrewe: null, // Clear subkrewe reference in member
            updated_at: new Date(),
          } as Member
        })

        // 3. Assign the member as captain of the SubKrewe
        const success = await subkrewesStore.assignCaptainToSubKrewe(
          subkrewe,
          this.members[memberPath], // Use the updated member from the store
        )

        if (!success) {
          // Roll back the member type change if SubKrewe assignment failed
          await updateDoc(memberRef, {
            member_type: member.member_type || 'member',
            updated_at: serverTimestamp(),
          })

          this.$patch((state) => {
            state.members[memberPath] = {
              ...state.members[memberPath],
              member_type: member.member_type || 'member',
              updated_at: new Date(),
            } as Member
          })

          return false
        }

        return true
      }
      catch (error) {
        console.error('Error promoting to captain:', error)
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Change a member's type (member, redshirt, captain)
     * Handles all state transitions and relationship updates
     * Updated to handle multiple captains
     */
    async changeMemberType(member: Member, newType: string): Promise<boolean> {
      // No change needed if type is the same
      if (member.member_type === newType) return true

      try {
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`
        const subkrewesStore = useSubKrewesStore()

        // STATE TRANSITION: Regular Member → Captain
        if (newType === 'captain') {
          // Ensure member is no longer part of any subkrewe
          if (member.subkrewe) {
            const oldSubkrewe = member.subkrewe

            // Update Firestore: make captain and remove subkrewe
            await updateDoc(memberRef, {
              member_type: 'captain',
              subkrewe: deleteField(),
              updated_at: serverTimestamp(),
            })

            // Update both stores atomically
            this.$patch((state) => {
              state.members[memberPath] = {
                ...state.members[memberPath],
                member_type: 'captain',
                subkrewe: null,
                updated_at: new Date(),
              } as Member
            })

            subkrewesStore.$patch((state) => {
              const oldSubkrewePath = `subkrewes/${oldSubkrewe.id}`
              if (state.subkrewes[oldSubkrewePath]?.members) {
                state.subkrewes[oldSubkrewePath].members
                  = state.subkrewes[oldSubkrewePath].members.filter(m => m.id !== member.id)
              }
            })
          }
          else {
            // Simple case: just update member type
            await updateDoc(memberRef, {
              member_type: 'captain',
              updated_at: serverTimestamp(),
            })

            this.$patch((state) => {
              state.members[memberPath] = {
                ...state.members[memberPath],
                member_type: 'captain',
                updated_at: new Date(),
              } as Member
            })
          }

          return true
        }

        // STATE TRANSITION: Captain → Regular Member
        if (member.member_type === 'captain' && newType !== 'captain') {
          const oldSubkrewe = member.subkrewe

          if (oldSubkrewe) {
            // Need to remove as captain from the subkrewe
            // This handles removing from the captains array
            await subkrewesStore.removeCaptainFromSubKrewe(oldSubkrewe, member, newType === 'member')
          }

          // Update the member type
          await updateDoc(memberRef, {
            member_type: newType,
            updated_at: serverTimestamp(),
          })

          this.$patch((state) => {
            state.members[memberPath] = {
              ...state.members[memberPath],
              member_type: newType,
              updated_at: new Date(),
            } as Member
          })

          return true
        }

        // Simple type change (member ↔ redshirt)
        await updateDoc(memberRef, {
          member_type: newType,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          state.members[memberPath] = {
            ...state.members[memberPath],
            member_type: newType,
            updated_at: new Date(),
          } as Member
        })

        return true
      }
      catch (error) {
        console.error('Error changing member type:', error)
        return false
      }
    }, /**
     * Toggle a member's admin status
     * @param member The member to update
     * @param isAdmin New admin status
     * @returns Promise<boolean> indicating success
     */
    async toggleAdminStatus(member: Member, isAdmin: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle admin status: invalid member object')
          return false
        }

        // Get Firestore instance - this is properly mocked in tests
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Create roles object if it doesn't exist
        const roles = member.roles || {}
        roles.admin = isAdmin

        // Update Firestore
        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.admin = isAdmin
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling admin status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Toggle a member's band admin status
     * @param member The member to update
     * @param isBandAdmin New band admin status
     * @returns Promise<boolean> indicating success
     */
    async toggleBandAdminStatus(member: Member, isBandAdmin: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle band admin status: invalid member object')
          return false
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Create roles object if it doesn't exist
        const roles = member.roles || {}
        roles.band_admin = isBandAdmin

        // Update Firestore
        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.band_admin = isBandAdmin
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling band admin status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Toggle a member's redshirt admin status
     * @param member The member to update
     * @param isRedshirtAdmin New redshirt admin status
     * @returns Promise<boolean> indicating success
     */
    async toggleRedshirtAdminStatus(member: Member, isRedshirtAdmin: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle redshirt admin status: invalid member object')
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        const roles = member.roles || {}
        roles.redshirt_admin = isRedshirtAdmin

        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.redshirt_admin = isRedshirtAdmin
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling redshirt admin status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Toggle a member's check-in volunteer status
     * @param member The member to update
     * @param isCheckinVolunteer New check-in volunteer status
     * @returns Promise<boolean> indicating success
     */
    async toggleCheckinVolunteerStatus(member: Member, isCheckinVolunteer: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle check-in volunteer status: invalid member object')
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        const roles = member.roles || {}
        roles.checkinVolunteer = isCheckinVolunteer

        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.checkinVolunteer = isCheckinVolunteer
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling check-in volunteer status for member ${member.id}:`, error)
        return false
      }
    },

    /**
    * Toggle a member's banned status
    * @param member The member to update
    * @param isBanned New banned status
    * @param banReason Reason for banning (required when banning, ignored when unbanning)
    * @returns Promise<boolean> indicating success
    */
    async toggleBanStatus(member: Member, isBanned: boolean, banReason?: string): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle ban status: invalid member object')
          return false
        }

        // Validate that a reason is provided when banning
        if (isBanned && !banReason) {
          console.error('Ban reason is required when banning a member')
          return false
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Prepare update data
        const updateData: Partial<MemberFirestoreData> = {
          banned: isBanned,
          updated_at: serverTimestamp(),
        }

        // Add ban reason and timestamp if banning
        if (isBanned) {
          updateData.banned_reason = banReason
          updateData.banned_at = serverTimestamp()
        }
        else {
          // Remove ban reason and timestamp when unbanning
          updateData.banned_reason = deleteField()
          updateData.banned_at = deleteField()
        }

        // If banning a member who belongs to a SubKrewe, remove them from the SubKrewe
        if (isBanned && member.subkrewe) {
          const subkrewesStore = useSubKrewesStore()

          // Handle captains differently from regular members
          if (member.member_type === 'captain') {
            await subkrewesStore.removeCaptainFromSubKrewe(member.subkrewe, member, false)
          }
          else {
            // Pass the current user ID and 'banned' as the reason
            const currentUser = useCurrentUser().value
            const currentUserId = currentUser?.uid
            await subkrewesStore.removeMemberFromSubKrewe(member.subkrewe, member, currentUserId, 'banned')
          }

          // Since we're removing from SubKrewe, we need to update the subkrewe reference
          updateData.subkrewe = deleteField()
        }

        // First call our API to disable/enable the user in Firebase Auth
        try {
          await withTokenRetry(async () => {
            // Use $fetch directly instead of useFetch for better post-mount behavior
            return await $fetch('/api/admin/toggle-user-ban', {
              method: 'POST',
              body: {
                userId: member.id,
                banned: isBanned,
                reason: banReason,
              },
            })
          })
        }
        catch (error) {
          console.error('Error calling ban API:', error)
          return false
        }

        // Then update Firestore
        await updateDoc(memberRef, updateData)

        // Update local state
        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }

          state.members[memberPath].banned = isBanned

          if (isBanned) {
            state.members[memberPath].banned_reason = banReason
            state.members[memberPath].banned_at = new Date()
          }
          else {
            delete state.members[memberPath].banned_reason
            delete state.members[memberPath].banned_at
          }

          // If banning, also remove SubKrewe reference in local state
          if (isBanned && member.subkrewe) {
            state.members[memberPath].subkrewe = null
          }

          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling ban status for member ${member.id}:`, error)
        return false
      }
    },

    /**
 * Toggle a member's lifetime status
 * @param member The member to update
 * @param isLifetime New lifetime status
 * @param reason Optional reason for granting lifetime status
 * @returns Promise<boolean> indicating success
 */
    async toggleLifetimeStatus(member: Member, isLifetime: boolean, reason?: string): Promise<boolean> {
      try {
        if (!member?.id) {
          throw new Error('Invalid member data')
        }

        // Validate that a reason is provided when granting lifetime status
        if (isLifetime && !reason) {
          throw new Error('A reason is required when granting lifetime status')
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Prepare update data
        const updateData: Partial<MemberFirestoreData> = {
          lifetime: isLifetime,
          updated_at: serverTimestamp(),
        }

        // Add lifetime reason and timestamp if granting lifetime status
        if (isLifetime) {
          updateData.lifetime_reason = reason
          updateData.lifetime_since = serverTimestamp()
        }
        else {
          // If removing lifetime status, remove reason and timestamp
          updateData.lifetime_reason = deleteField()
          updateData.lifetime_since = deleteField()
        }

        // Update Firestore
        await updateDoc(memberRef, updateData)

        // Update local state
        this.$patch((state) => {
          if (state.members[memberPath]) {
            state.members[memberPath] = {
              ...state.members[memberPath],
              lifetime: isLifetime,
              lifetime_reason: isLifetime ? reason : undefined,
              lifetime_since: isLifetime ? new Date() : undefined,
            }
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling lifetime status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Subscribe to the current member document for real-time updates
     * @returns Unsubscribe function
     */
    subscribeToCurrentMember(): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) {
        console.warn('[Members] Cannot subscribe to current member: No authenticated user')
        return null
      }

      const { registerSubscription, hasSubscriptionWithKey, clearSubscriptionByKey } = useSubscriptions()
      const db = useFirestore()
      const memberRef = doc(db, 'members', user.uid)

      // Create a unique key for this subscription
      const subKey = `current-member-${user.uid}`

      // Check if we already have this subscription
      if (hasSubscriptionWithKey(subKey)) {
        console.warn('[Members] Subscription to current member already exists, replacing')
        // Clear the existing subscription to prevent duplicates
        clearSubscriptionByKey(subKey)
      }

      // Setting up subscription for current member

      // Set up initial state tracking
      const memberPath = `members/${user.uid}`
      const currentMember = this.members[memberPath]
      if (currentMember && !currentMember._lastSyncedType) {
        this.$patch((state) => {
          if (state.members[memberPath]) {
            state.members[memberPath]._lastSyncedType = currentMember.member_type
          }
        })
      }

      // Subscribe to real-time updates for this member
      const unsubscribe = onSnapshot(memberRef, (docSnapshot) => {
        if (!docSnapshot.exists()) {
          console.warn(`[Members] Current member document doesn't exist: ${user.uid}`)
          return
        }

        // Convert to Member object
        const data = docSnapshot.data() as MemberFirestoreData
        const member = this.convertToMember(user.uid, data, false)

        // Resolve subkrewe reference
        this.resolveSubkreweForMember(data.subkrewe).then((subkrewe) => {
          // Capture previous state BEFORE updating
          const previousMember = this.members[memberPath]
          const previousType = previousMember?._lastSyncedType
          const previousSubkreweId = previousMember?.subkrewe?.id

          // Update local state with the new member data - USING SYNCHRONOUS CODE ONLY
          this.$patch((state) => {
            // Update member in state - ONLY SYNCHRONOUS OPERATIONS HERE
            member.subkrewe = subkrewe
            member._lastSyncedType = member.member_type
            state.members[memberPath] = member
          })

          // Check if member type changed - OUTSIDE OF $PATCH
          if (previousType && previousType !== member.member_type) {
            // Member type changed

            // If becoming captain, ensure subkrewe relationship is correct
            if (member.member_type === 'captain' && subkrewe) {
              const subkrewesStore = useSubKrewesStore()
              subkrewesStore.ensureCaptainSynchronized(subkrewe, member)
                .catch((error) => {
                  console.error('[Members] Error synchronizing captain relationship:', error)
                })
            }
          }

          // Check if subkrewe changed - OUTSIDE OF $PATCH
          if (previousSubkreweId !== member.subkrewe?.id) {
            // Member subkrewe changed

            this.syncSubkreweRelationship(
              previousMember || member,
              member.subkrewe!,
            ).catch((error) => {
              console.error('[Members] Error syncing subkrewe relationship:', error)
            })
          }
        })
      }, (error) => {
        // Note: permission-denied errors on initial load are benign - Firebase SDK
        // retries automatically. This is due to auth token propagation timing.
        console.error('[Members] Error in member subscription:', error)
      })

      // Register the subscription
      registerSubscription(unsubscribe, 'member', subKey)

      return unsubscribe
    },

    /**
    * Subscribe to a filtered set of members
    * @param queryConstraints - Firestore query constraints to filter members
    * @param options - Additional options (pagination, etc.)
    * @returns Unsubscribe function
    */
    subscribeToMembers(
      queryConstraints: QueryConstraint[] = [],
      options: {
        limit?: number
        category?: string // For subscription tracking
      } = {},
    ): (() => void) | null {
      const { registerSubscription } = useSubscriptions()
      const db = useFirestore()

      // Default options
      const queryLimit = options.limit || 50
      const category = options.category || 'members-query'

      // Base query - collection and ordering
      let membersQuery = query(
        collection(db, 'members'),
        orderBy('name'),
        ...queryConstraints,
      )

      // Add pagination if specified
      if (queryLimit) {
        membersQuery = query(membersQuery, limit(queryLimit))
      }

      // console.warn(`[Members] Setting up subscription for filtered members (${category})`)

      // Set up the subscription
      const unsubscribe = onSnapshot(membersQuery, (snapshot) => {
        // console.warn(`[Members] Snapshot update with ${snapshot.docChanges().length} changes`)

        // Process changes
        const changes: Record<string, Member> = {}
        const removals: string[] = []

        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added' || change.type === 'modified') {
            const data = change.doc.data() as MemberFirestoreData
            const member = this.convertToMember(change.doc.id, data, false)

            // Store for batch update
            const memberPath = `members/${change.doc.id}`
            changes[memberPath] = member

            // Resolve subkrewe later after batch update
            this.resolveSubkreweForMember(data.subkrewe).then((subkrewe) => {
              if (this.members[memberPath]) {
                this.$patch((state) => {
                  if (state.members[memberPath]) {
                    state.members[memberPath].subkrewe = subkrewe
                  }
                })
              }
            }).catch((error) => {
              console.error(`[Members] Error resolving subkrewe for ${change.doc.id}:`, error)
            })

            // console.warn(`[Members] ${change.type === 'added' ? 'Added' : 'Updated'} member ${member.name} (${member.id})`)
          }
          else if (change.type === 'removed') {
            const memberPath = `members/${change.doc.id}`
            removals.push(memberPath)
            // console.warn(`[Members] Removed member ${change.doc.id}`)
          }
        })

        // Apply all changes in a single patch for better performance
        this.$patch((state) => {
          // Add or update members
          Object.entries(changes).forEach(([path, member]) => {
            state.members[path] = member
          })

          // Remove deleted members
          removals.forEach((path) => {
            delete state.members[path]
          })
        })
      }, (error) => {
        console.error(`[Members] Error in subscription (${category}):`, error)
      })

      // Register the subscription
      registerSubscription(unsubscribe, category)

      return unsubscribe
    },

    // Update the setupSubscriptions method to be more testable
    setupSubscriptions() {
      // IMPORTANT: Initialize _lastSyncedType for any members that don't have it
      // This is needed to prevent infinite loops when synchronizing with SubKrewes
      Object.values(this.members).forEach((member) => {
        if (member._lastSyncedType === undefined) {
          this.$patch((state) => {
            const memberPath = `members/${member.id}`
            if (state.members[memberPath]) {
              state.members[memberPath]._lastSyncedType = member.member_type
            }
          })
        }
      })

      // Subscribe to member changes to sync with subkrewes
      this.$subscribe((_mutation, _state) => {
        try {
          // Don't use useCurrentUser() directly in the subscription
          // Instead check if we're in a test environment or get the current user safely
          let currentUserId: null | undefined | string = null

          try {
            // This might be undefined in tests
            const currentUser = useCurrentUser().value
            currentUserId = currentUser?.uid
          }
          catch {
            // Silently handle errors in test environment
            // Could not get current user in store subscription - likely in test environment
          }

          // Skip synchronization if we don't have a valid user ID
          if (!currentUserId) return

          // Rest of the subscription logic remains the same...
        }
        catch (error) {
          console.error('[Members] Error in subscription handler:', error)
        }
      }, { flush: 'sync' })
    },

    /**
     * Add a child to a member
     */
    async addChildToMember(member: Member, child: { id: string, firstName: string, lastName: string, birthday: unknown }): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot add child: invalid member object')
          return false
        }

        // Block redshirts from child mutations
        if (isRedshirt(member)) {
          console.warn(`[Members] Blocked addChildToMember for redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)

        // Extract ID from child and create rest of data
        const { id, ...childData } = child
        const childToAdd = {
          ...childData,
          birthday: typeof childData.birthday === 'string' ? childData.birthday : new Date(),
          created_at: new Date(),
        }

        // Update just this child's entry in the map
        await updateDoc(memberRef, {
          [`children.${id}`]: childToAdd,
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          if (state.members[memberPath]) {
            if (!state.members[memberPath].children) {
              state.members[memberPath].children = {}
            }
            state.members[memberPath].children[id] = childToAdd
            state.members[memberPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error adding child to member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Update a child's information
     */
    async updateChildForMember(member: Member, child: { id: string, firstName: string, lastName: string, birthday: unknown }): Promise<boolean> {
      try {
        if (!member?.id || !child.id) {
          console.error('Cannot update child: invalid member or child object')
          return false
        }

        // Block redshirts from child mutations
        if (isRedshirt(member)) {
          console.warn(`[Members] Blocked updateChildForMember for redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)

        // Extract ID from child
        const { id, ...childData } = child

        // Update just this child's entry in the map
        await updateDoc(memberRef, {
          [`children.${id}`]: {
            ...childData,
            birthday: typeof childData.birthday === 'string' ? childData.birthday : new Date(),
          },
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          if (state.members[memberPath]) {
            if (!state.members[memberPath].children) {
              state.members[memberPath].children = {}
            }
            state.members[memberPath].children[id] = {
              ...childData,
              birthday: typeof childData.birthday === 'string' ? childData.birthday : new Date(),
            }
            state.members[memberPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error updating child for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Remove a child from a member
     */
    async removeChildFromMember(member: Member, childId: string): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot remove child: invalid member object')
          return false
        }

        // Block redshirts from child mutations
        if (isRedshirt(member)) {
          console.warn(`[Members] Blocked removeChildFromMember for redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)

        // Delete this child's entry from the map
        await updateDoc(memberRef, {
          [`children.${childId}`]: deleteField(),
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          if (state.members[memberPath]?.children) {
            const updatedChildren = { ...state.members[memberPath].children }
            delete updatedChildren[childId]
            state.members[memberPath].children = updatedChildren
            state.members[memberPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error removing child from member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Delete a member completely, including their auth account
     * @param member The member to delete
     * @returns Promise<boolean> indicating success
     */
    async deleteUser(member: Member): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot delete user: invalid member object')
          return false
        }

        // First call our API to delete the user from Firebase Auth
        try {
          await withTokenRetry(async () => {
            return await $fetch('/api/admin/delete-user', {
              method: 'POST',
              body: {
                userId: member.id,
              },
            })
          })
        }
        catch (error) {
          console.error('Error calling delete user API:', error)
          return false
        }

        // Remove from subkrewe if applicable
        if (member.subkrewe) {
          const subkrewesStore = useSubKrewesStore()
          // Handle captains vs regular members
          if (member.member_type === 'captain') {
            await subkrewesStore.removeCaptainFromSubKrewe(member.subkrewe, member, false)
          }
          else {
            const currentUser = useCurrentUser().value
            const currentUserId = currentUser?.uid
            await subkrewesStore.removeMemberFromSubKrewe(member.subkrewe, member, currentUserId, 'deleted')
          }
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Delete the document from Firestore
        await deleteDoc(memberRef)

        // Update local state
        this.$patch((state) => {
          delete state.members[memberPath]
        })

        return true
      }
      catch (error) {
        console.error(`Error deleting user ${member.id}:`, error)
        return false
      }
    },

    /**
     * Delete only the member document from Firestore (for self-deletion)
     * Used when a user deletes their own account - they handle auth deletion client-side
     * @param memberId The member ID to delete
     * @returns Promise<boolean> indicating success
     */
    async deleteMemberDocument(memberId: string): Promise<boolean> {
      try {
        const memberPath = `members/${memberId}`
        const member = this.members[memberPath]

        // Remove from subkrewe if applicable
        if (member?.subkrewe) {
          const subkrewesStore = useSubKrewesStore()
          if (member.member_type === 'captain') {
            await subkrewesStore.removeCaptainFromSubKrewe(member.subkrewe, member, false)
          }
          else {
            await subkrewesStore.removeMemberFromSubKrewe(member.subkrewe, member, memberId, 'deleted')
          }
        }

        // Delete the document from Firestore
        const db = getFirestore()
        const memberRef = doc(db, 'members', memberId)
        await deleteDoc(memberRef)

        // Update local state
        this.$patch((state) => {
          delete state.members[memberPath]
        })

        return true
      }
      catch (error) {
        console.error(`Error deleting member document ${memberId}:`, error)
        return false
      }
    },
  },
})
